import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "@/lib/api";
import { API_PATHS } from "@/lib/api-paths";

const STORAGE_KEY = "simulation_access_token";

type AuthContextValue = {
  token: string | null;
  login: (username: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function getStoredToken(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage.getItem(STORAGE_KEY);
}

function setStoredToken(token: string | null) {
  if (typeof sessionStorage === "undefined") return;
  if (token) sessionStorage.setItem(STORAGE_KEY, token);
  else sessionStorage.removeItem(STORAGE_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(getStoredToken);

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.post<{ access?: string; error?: string }>(
      API_PATHS.AUTH_LOGIN,
      { body: { username, password } }
    );
    if (res.ok && res.data.access) {
      setToken(res.data.access);
      setStoredToken(res.data.access);
      return { ok: true };
    }
    return {
      ok: false,
      error: (res.data as { error?: string }).error || `HTTP ${res.status}`,
    };
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setStoredToken(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      login,
      logout,
      isAuthenticated: !!token,
    }),
    [token, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
